import { z } from "zod";
import { UnpaidFigures } from "./payment.js";
import { TaxCategory } from "./tax.js";

// ---------------------------------------------------------------------------
// 仕入と支払予定（注文書の3回目 3a、#233、docs/93 §15・§15.10、../eigyouryoku/docs/04 §2.6＝営業力 E5-1 と同じ）。DB 20261026_1100
//   仕入＝相手からの請求（または支払通知書）1通。入口は3つ：承諾済の{order}の「⋯」→「仕入登録」／受け取った書類（書類「請求書」）の
//   AI読み取り結果の下「仕入登録」／左のメニュー「仕入」→「仕入登録」（{order}なし）。両製品。門はモジュール sales。
//   状態：下書き → 確定（支払予定と{project}の原価に入る）→（3c で支払済）。取消は voided_at。
//   金額・税額は相手の請求書のとおり（再計算しない）。下書きの消費税は明細から税率ごとに1回（切り捨て）で入れ、打ったら自動を外す。
//   支払期日は{vendor}の支払条件と受領日（無ければ請求日）から。受領から 60 日を超えると朱（取適法。止めない）。
//   言葉：仕入・支払予定・未払い・原価計上・確定（docs/93 §15.2・§15.10）。{vendor}に「御中」を付けない（相手から届いた請求書＝こちらが宛先）
// ---------------------------------------------------------------------------

export const PURCHASE_LIMITS = {
  lines: 300,
  name: 200,
  spec: 200,
  unit: 20,
  note: 200,
  invoiceNo: 60,
  memo: 2000,
  /** 金額の上限（1行・消費税。100 億円未満） */
  amount: 9_999_999_999,
  /** 取適法の支払期日（受領日から） */
  payDays: 60,
  /** ホームの「支払予定」に出す期日（今日から 7 日以内と期限超過） */
  dueSoonDays: 7,
} as const;

export const PurchaseStatus = z.enum(["draft", "confirmed", "voided"]);
export type PurchaseStatus = z.infer<typeof PurchaseStatus>;
/** 一覧・チップの状態（下書き／未払い／一部支払／期限超過／支払済／取消）。一部支払・支払済は残から（#233 3c） */
export const PurchasePayState = z.enum(["draft", "unpaid", "partial", "overdue", "paid", "voided"]);
export type PurchasePayState = z.infer<typeof PurchasePayState>;
export const PURCHASE_STATE_LABEL: Record<PurchasePayState, string> = {
  draft: "下書き",
  unpaid: "未払い",
  partial: "一部支払",
  overdue: "期限超過",
  paid: "支払済",
  voided: "取消",
};

/**
 * 一覧のタブ（#233 3c）：未払い＝確定して残のある仕入（一部支払も含む＝ホームの今月の「未払い」と同じ数）／一部支払＝そのうち支払の記録があるもの／
 * 支払済＝残 0／下書き／すべて
 */
export const PurchaseTab = z.enum(["draft", "unpaid", "partial", "paid", "all"]);
export type PurchaseTab = z.infer<typeof PurchaseTab>;
export const PURCHASE_TAB_LABEL: Record<PurchaseTab, string> = { draft: "下書き", unpaid: "未払い", partial: "一部支払", paid: "支払済", all: "すべて" };

export const PurchaseLineKind = z.enum(["detail", "heading"]);
export type PurchaseLineKind = z.infer<typeof PurchaseLineKind>;

const Yen = z.number().int().min(-PURCHASE_LIMITS.amount).max(PURCHASE_LIMITS.amount);
const YmdZ = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** 明細の1行（画面の形）。orderLine＝{order}の行（2段目に出す「{order} PO-0001 2 台 × 380,000 ＝ 760,000」） */
export const PurchaseLine = z.object({
  id: z.string().uuid(),
  kind: PurchaseLineKind,
  name: z.string(),
  spec: z.string(),
  quantity: z.number().nullable(),
  unit: z.string(),
  unitPrice: z.number().int().nullable(),
  amount: z.number().int(),
  taxCategory: TaxCategory,
  note: z.string(),
  itemId: z.string().uuid().nullable(),
  orderLineId: z.string().uuid().nullable(),
  orderLine: z
    .object({ no: z.number().int(), quantity: z.number().nullable(), unit: z.string(), unitPrice: z.number().int().nullable(), amount: z.number().int() })
    .nullable(),
});
export type PurchaseLine = z.infer<typeof PurchaseLine>;

/** 保存する1行（PUT /purchases/:id の lines）。並びはこの順（行の番号は API が振る） */
export const PurchaseLineInput = z.object({
  id: z.string().uuid(),
  kind: PurchaseLineKind.default("detail"),
  name: z.string().max(PURCHASE_LIMITS.name).default(""),
  spec: z.string().max(PURCHASE_LIMITS.spec).default(""),
  quantity: z.number().min(-9_999_999_999).max(9_999_999_999).nullable().default(null),
  unit: z.string().max(PURCHASE_LIMITS.unit).default(""),
  unitPrice: Yen.nullable().default(null),
  amount: Yen.default(0),
  taxCategory: TaxCategory.default("standard"),
  note: z.string().max(PURCHASE_LIMITS.note).default(""),
  itemId: z.string().uuid().nullable().default(null),
  orderLineId: z.string().uuid().nullable().default(null),
});
export type PurchaseLineInput = z.infer<typeof PurchaseLineInput>;

/** 仕入の画面の形（GET /purchases/:id） */
export const Purchase = z.object({
  id: z.string().uuid(),
  status: PurchaseStatus,
  payState: PurchasePayState,
  vendor: z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** 略称（無ければ正式名） */
    displayName: z.string(),
    /** 登録番号（T＋13桁）。無ければ null＝免税事業者の扱い（控除割合） */
    registrationNo: z.string().nullable(),
    qualified: z.boolean(),
  }),
  group: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  order: z.object({ id: z.string().uuid(), displayNo: z.string().nullable(), subject: z.string(), subtotal: z.number().int(), total: z.number().int() }).nullable(),
  /** 原本（受け取った書類）。url は原本を開く署名つきの道 */
  received: z.object({ id: z.string().uuid(), title: z.string(), groupId: z.string().uuid(), url: z.string().nullable() }).nullable(),
  /** 元の支払通知書（#233 3b。確認済で起こした仕入の原本＝その PDF。name は発行のときに固めた帳票の名前） */
  paymentNotice: z.object({ id: z.string().uuid(), displayNo: z.string().nullable(), name: z.string() }).nullable().default(null),
  /** 請求日 */
  purchasedOn: z.string(),
  vendorInvoiceNo: z.string(),
  receivedOn: z.string().nullable(),
  inspectedOn: z.string().nullable(),
  paymentDueOn: z.string().nullable(),
  /** 支払期日を{vendor}の支払条件から入れているか（打つと外れる） */
  paymentDueAuto: z.boolean(),
  /** 受領日から支払期日までの日数が 60 を超えるときの日数（超えなければ null） */
  over60Days: z.number().int().nullable(),
  lines: z.array(PurchaseLine),
  subtotal: z.number().int(),
  taxAmount: z.number().int(),
  /** 消費税を明細から入れているか（打つと外れる＝再計算しない） */
  taxAuto: z.boolean(),
  /** 明細から計算した消費税（打った額と比べる。計算できなければ null） */
  taxSuggested: z.number().int().nullable(),
  /** 明細の税率が1つならその率（0.1 の形。「消費税（10%）」）。2つ以上・無ければ null */
  taxRate: z.number().nullable(),
  total: z.number().int(),
  /** 控除割合（免税事業者からの仕入）。確定した仕入は固めた値、下書きは今の見込み。登録番号があれば null */
  creditRatio: z.number().nullable(),
  /** 控除割合の期間の終わり（「70%（経過措置 ～2028/9）」） */
  creditUntil: z.string().nullable(),
  costPosting: z.boolean(),
  /** {order}との差（仕入の小計 − {order}の小計。{order}が無ければ null） */
  orderDiff: z.number().int().nullable(),
  /** 消し込んだ額（支払の当てた額＋先方負担の振込手数料＋源泉徴収。#233 3c）。下書き・取消は 0 */
  paid: z.number().int().default(0),
  balance: z.number().int(),
  overdueDays: z.number().int(),
  memo: z.string(),
  confirmedAt: z.string().nullable(),
  /** 下書きか（明細・日付・金額を直せる） */
  canEdit: z.boolean(),
  updatedAt: z.string(),
});
export type Purchase = z.infer<typeof Purchase>;

/** POST /purchases：{vendor}と{project}だけ・{order}から（明細を写す）・受け取った書類から（原本に付ける）。{vendor}か{order}が要る */
export const PurchaseCreate = z
  .object({
    vendorId: z.string().uuid().optional(),
    projectGroupId: z.string().uuid().nullable().optional(),
    fromOrderId: z.string().uuid().optional(),
    fromReceivedId: z.string().uuid().optional(),
  })
  .refine((v) => !!v.vendorId || !!v.fromOrderId, { message: "{vendor}を選んでください", path: ["vendorId"] });
export type PurchaseCreate = z.infer<typeof PurchaseCreate>;

/** PUT /purchases/:id（版つき。違えば 409 PURCHASE_STALE）。省いた欄は変えない。確定した仕入は 原価計上・付け先・メモ だけ */
export const PurchaseSave = z.object({
  version: z.string(),
  vendorId: z.string().uuid().optional(),
  projectGroupId: z.string().uuid().nullable().optional(),
  purchasedOn: YmdZ.optional(),
  vendorInvoiceNo: z.string().trim().max(PURCHASE_LIMITS.invoiceNo).optional(),
  receivedOn: YmdZ.nullable().optional(),
  inspectedOn: YmdZ.nullable().optional(),
  /** 支払期日。null＝{vendor}の支払条件から入れ直す（自動） */
  paymentDueOn: YmdZ.nullable().optional(),
  /** 消費税。null＝明細から入れ直す（自動） */
  taxAmount: Yen.nullable().optional(),
  costPosting: z.boolean().optional(),
  memo: z.string().max(PURCHASE_LIMITS.memo).optional(),
  lines: z.array(PurchaseLineInput).max(PURCHASE_LIMITS.lines).optional(),
});
export type PurchaseSave = z.infer<typeof PurchaseSave>;

export const PurchaseVoid = z.object({ reason: z.string().trim().max(200).default("") });
export type PurchaseVoid = z.infer<typeof PurchaseVoid>;

/** 一覧の1行 */
export const PurchaseSummary = z.object({
  id: z.string().uuid(),
  status: PurchaseStatus,
  payState: PurchasePayState,
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  vendorInvoiceNo: z.string(),
  purchasedOn: z.string(),
  group: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  orderNo: z.string().nullable(),
  subtotal: z.number().int(),
  total: z.number().int(),
  paymentDueOn: z.string().nullable(),
  /** 消し込んだ額（#233 3c） */
  paid: z.number().int().default(0),
  balance: z.number().int(),
  overdueDays: z.number().int(),
  over60: z.boolean(),
  updatedAt: z.string(),
});
export type PurchaseSummary = z.infer<typeof PurchaseSummary>;
export const PurchaseList = z.object({
  items: z.array(PurchaseSummary),
  counts: z.object({ draft: z.number().int(), unpaid: z.number().int(), partial: z.number().int().default(0), paid: z.number().int(), all: z.number().int() }),
  /** 表示中のタブの合計（税込）と残 */
  total: z.number().int(),
  balance: z.number().int(),
});
export type PurchaseList = z.infer<typeof PurchaseList>;

/** ホームの「支払予定」（期日 7 日以内と期限超過。期限超過が上・次に期日の近い順） */
export const HomePurchasesDue = z.object({
  count: z.number().int(),
  total: z.number().int(),
  overdueCount: z.number().int(),
  overdueTotal: z.number().int(),
  rows: z.array(
    z.object({
      purchaseId: z.string().uuid(),
      /** 行の「支払登録」の窓に渡す（#233 3c） */
      vendorId: z.string().uuid().optional(),
      vendorName: z.string(),
      vendorInvoiceNo: z.string(),
      groupName: z.string(),
      dueDate: z.string().nullable(),
      balance: z.number().int(),
      state: z.enum(["unpaid", "partial", "overdue"]),
      overdueDays: z.number().int(),
    }),
  ),
});
export type HomePurchasesDue = z.infer<typeof HomePurchasesDue>;

/** {project}の数字の枠「仕入」「粗利」（GET /groups/:id/margin と案件の詳細の summary） */
export const GroupMargin = z.object({
  /** 確定した仕入（税込）と、そのうち期限超過 */
  purchases: z.object({ count: z.number().int(), total: z.number().int(), overdueTotal: z.number().int() }),
  /** 発行した{order}（取消・下書きは除く）と、仕入の無い{order} */
  orders: z.object({ count: z.number().int(), total: z.number().int(), withoutPurchase: z.number().int(), withoutPurchaseTotal: z.number().int() }),
  /** 売上（受注の税抜）・原価（確定した仕入の税抜）・粗利。reliable＝仕入が1件でも入っている（false のあいだ粗利は「—」） */
  sales: z.number().int(),
  cost: z.number().int(),
  margin: z.number().int().nullable(),
  rate: z.number().nullable(),
  reliable: z.boolean(),
});
export type GroupMargin = z.infer<typeof GroupMargin>;

/** 取引先の詳細の「未払い」（確定した仕入の残） */
export const PayableFigures = UnpaidFigures;
export type PayableFigures = UnpaidFigures;

/**
 * 明細の金額＝数量×単価（円未満は四捨五入。相手の請求書の額に打ち直せる＝この値は既定）。どちらかが無ければ null。
 * 数量は小数 4 桁まで（DB の numeric(14,4)）＝10,000 倍の整数にしてから掛ける（浮動小数点の誤差を出さない）
 */
export function purchaseLineAmount(quantity: number | null, unitPrice: number | null): number | null {
  if (quantity == null || unitPrice == null) return null;
  const q = Math.round(quantity * 10000);
  const v = (q * unitPrice) / 10000;
  return v < 0 ? -Math.round(-v) : Math.round(v);
}

/** 受領日から支払期日までの日数（60 を超えるときだけ。どちらかが無ければ null） */
export function purchaseOver60(receivedOn: string | null, dueOn: string | null): number | null {
  if (!receivedOn || !dueOn) return null;
  const d = Math.round((Date.parse(`${dueOn}T00:00:00Z`) - Date.parse(`${receivedOn}T00:00:00Z`)) / 86_400_000);
  return d > PURCHASE_LIMITS.payDays ? d : null;
}

/** 控除割合の言い方：「70%（経過措置 ～2028/9）」。登録番号があれば null（全額） */
export function creditRatioText(ratio: number | null, until: string | null): string | null {
  if (ratio == null) return null;
  const pct = `${Math.round(ratio * 1000) / 10}%`;
  return until ? `${pct}（経過措置 ～${Number(until.slice(0, 4))}/${Number(until.slice(5, 7))}）` : `${pct}（経過措置）`;
}

/** 画面・API の文（{vendor}・{project}・{order} は会社の言葉＝画面は t()、API は fillLabels） */
export const PURCHASE_MSG = {
  menu: "仕入",
  title: "仕入",
  list: "仕入",
  create: "仕入登録",
  newTitle: "仕入登録",
  notFound: "その仕入は見つかりません",
  vendorRequired: "{vendor}を選んでください",
  vendorInvalid: "仕入の相手は{vendor}だけです",
  stale: "他の利用者が変更しました。再読込しますか？",
  locked: "確定した仕入は修正できません。下書きに戻してから修正してください",
  confirm: "確定",
  confirmed: "仕入を確定しました。支払予定と原価に入りました。",
  confirmTitle: "仕入の確定",
  confirmOver60: "支払期日が受領日から {n} 日後です（取適法の 60 日を超えています）。このまま確定しますか？",
  unconfirm: "下書きに戻す",
  unconfirmed: "下書きに戻しました。",
  void: "取消",
  voidTitle: "仕入の取消",
  voidConfirm: "この仕入を取消します。支払予定と原価から外れます。",
  voided: "仕入を取消しました。",
  deleteDraft: "下書き削除",
  deleted: "仕入の下書きを削除しました。",
  noLines: "明細が1行もありません。行を追加してから確定してください",
  notDraft: "下書きの仕入だけ確定できます",
  orderNotAccepted: "承諾済の{order}から仕入を登録できます",
  fromOrder: "仕入登録",
  saving: "保存中…",
  autoSaved: "自動保存 {at}",
  /** 頭の帯 */
  billDate: "請求日",
  invoiceNo: "請求書番号",
  project: "{project}",
  origin: "元",
  original: "原本",
  totalInTax: "合計（税込）",
  detail: "詳細",
  memo: "社内メモ",
  /** 条件の帯 */
  terms: "条件",
  receivedOn: "受領日",
  inspectedOn: "検査完了日",
  dueOn: "支払期日",
  dueAuto: "{vendor}の支払条件から",
  over60: (n: number) => `60日超（受領から${n}日）`,
  registration: "登録番号",
  registrationNone: "なし（免税事業者）",
  /** 条件の帯（1行に収める）。全文は title */
  registrationNoneShort: "なし",
  credit: "控除割合",
  creditFull: "—（全額）",
  helpCredit: "登録番号の無い{vendor}（免税事業者）からの仕入は、仕入税額控除が経過措置の割合になります（2026年10月から 70%）。確定したときの割合を記録します。金額は変わりません。",
  helpDue: "支払期日は{vendor}の支払条件と受領日（無ければ請求日）から入ります。打つと自動で変わらなくなります。受領日から60日を超えると朱で表示します（取適法）。",
  /** 明細 */
  addLine: "＋ 行追加",
  addHeading: "見出し追加",
  linesFrom: (n: number, no: string | null) => (no ? `${n} 行 {order} ${no} から` : `${n} 行`),
  colOrigin: "元",
  originOrder: (no: number) => `{order} 行 ${no}`,
  originOwn: "追加",
  lineMenu: "「{name}」の操作",
  lineAddBelow: "下に追加",
  lineUp: "上へ",
  lineDown: "下へ",
  lineDelete: "削除",
  headingPlaceholder: "見出し",
  subtotal: "小計（税抜）",
  tax: "消費税",
  taxRate: (pct: number) => `消費税（${pct}%）`,
  total: "合計",
  taxAutoBack: "自動に戻す",
  taxTyped: "相手の請求書の額",
  helpTax: "消費税は明細から税率ごとに計算して入ります。相手の請求書の額と違うときは打ち直してください（打った額は再計算しません）。",
  /** 下の帯 */
  costPosting: "原価計上",
  costTarget: "{project}",
  cost: "原価（税抜）",
  orderDiff: "{order}との差",
  helpCostPosting: "確定すると、この仕入（税抜）が{project}の原価に入り、粗利が出ます。外すと原価に含めません。",
  noProject: "{project}なし",
  /** 一覧 */
  empty: "仕入はありません",
  searchLabel: "{vendor}・請求書番号で絞り込み",
  tabsLabel: "仕入を状態で絞る",
  colBillDate: "請求日",
  colVendor: "{vendor}",
  colInvoiceNo: "請求書番号",
  colProject: "{project}",
  colAmount: "金額（税込）",
  colDue: "支払期日",
  colBalance: "残",
  colState: "状態",
  sumLine: (n: number) => `${n} 件`,
  /** {order}の「⋯」 */
  orderMenuCreate: "仕入登録",
  /** 受け取った書類 */
  receivedCreate: "仕入登録",
  receivedOrder: "{order}",
  receivedNoOrder: "{order}なし",
  receivedVendor: "{vendor}",
  receivedVendorPick: "{vendor}選択",
  receivedCreated: "仕入の下書きを登録しました。",
  receivedVendorRequired: "{vendor}を選んでください（差出人が{vendor}に見つかりませんでした）",
  /** 引き出しの下の段の注意書き（請求書のとき。見積の「見積にしてください」の代わり） */
  receivedNotice: "AI が読み取った内容です。原本と見比べてから登録してください。",
  /** ホーム */
  homeTitle: "支払予定",
  homeHelp: "確定した仕入のうち、支払期日が7日以内のものと、期日を過ぎたものです。期日を過ぎたものが上に「期限超過 n日」の文字で出ます。支払を登録して残額が 0 になると、ここから外れます。",
  homeAll: (n: number) => `未払いの仕入をすべて表示（${n}件）`,
  monthPayable: "未払い",
  helpMonthPayable: "未払いは、確定した仕入の残額の合計です（支払予定と同じ数え方）。",
  /** 今月の実績の「支払」（#233 3c）：その月に登録した支払の額の合計（取消を除く） */
  monthPaid: "支払",
  helpMonthPaid: "支払は、その月の支払日で登録した支払の額（振り込んだ額）の合計です（取消した支払は除く）。",
  /** {project}の画面 */
  groupTab: "仕入",
  tilePurchases: "仕入",
  tileMargin: "粗利",
  purchasesMissing: "仕入未入力",
  ordersWithout: (n: number, total: string) => `{order} ${n} 件　${total} 円`,
  ordersWithoutNote: "（仕入未入力）",
  helpPurchases: "確定した仕入の合計（税込）です。期日を過ぎた残額があれば「うち期限超過」に出ます。仕入が無く{order}があるときは、{order}の件数と合計（税込）を出します。",
  helpMargin: "受注した見積の合計（税抜）から、確定した仕入（税抜）を引いた額です。仕入が1件も無いあいだは出しません（「—」）。{order}の額からの見込みは出しません。",
  helpFiguresTax: "受注・請求済・未入金・仕入の金額は税込、粗利だけ税抜です。",
  /** 取引先の詳細 */
  partnerPayable: "未払い",
} as const;
